import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const downloadTemplate = async () => {
  const { data } = await api.get("/users/import/template?fmt=xlsx", { responseType: "blob" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = "omi-staff-template.xlsx"; a.click();
};

function downloadPasswords(rows) {
  const csv = ["employee_id,name,role,temp_password", ...rows.map((r) => `${r.employee_id},"${r.name}",${r.role},${r.temp_password}`)].join("\n");
  const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "omi-staff-passwords.csv"; a.click();
}

function PreviewTable({ rows }) {
  return (
    <div className="max-h-64 overflow-auto border rounded-md text-xs">
      <table className="w-full"><thead className="bg-muted sticky top-0"><tr>{["Row", "Employee ID", "Name", "Role", "Business", "Problem"].map((h) => <th key={h} className="text-left p-1.5">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r.row} className={r.ok ? "" : "bg-rose-50"} data-testid="staff-preview-row">
          <td className="p-1.5">{r.row}</td><td className="p-1.5 font-mono">{r.employee_id}</td><td className="p-1.5">{r.name}</td><td className="p-1.5">{r.role}</td><td className="p-1.5">{r.business}</td>
          <td className={`p-1.5 ${r.ok ? "text-emerald-700" : "text-rose-700"}`}>{r.errors.join("; ") || "OK"}</td></tr>)}</tbody></table>
    </div>
  );
}

export function StaffImport({ onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (path) => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { return (await api.post(path, fd)).data; } catch (ex) { toast.error(apiError(ex.response?.data?.detail)); return null; } finally { setBusy(false); }
  };
  const doPreview = async () => { const d = await send("/users/import/preview"); if (d) { setPreview(d); setCreated(null); } };
  const doImport = async () => { const d = await send("/users/import"); if (d) { setCreated(d.created); setPreview(null); toast.success(`${d.created.length} staff added`); onDone(); } };
  const reset = (o) => { if (!o) { setFile(null); setPreview(null); setCreated(null); } };
  return (
    <Dialog onOpenChange={reset}>
      <DialogTrigger asChild><Button variant="outline" data-testid="staff-import-btn"><Upload className="h-4 w-4 mr-1.5" />Import staff</Button></DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="staff-import-dialog">
        <DialogHeader><DialogTitle>Import staff from Excel / CSV</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Columns: name, employee_id, role, team_leader_employee_id, business (insurance / loans / both), email, phone. Nothing is saved until you press Add.</p>
        <div className="flex gap-2 flex-wrap items-center">
          <Button size="sm" variant="ghost" onClick={downloadTemplate} data-testid="staff-template-btn"><Download className="h-4 w-4 mr-1" />Template</Button>
          <Input type="file" accept=".xlsx,.csv" className="w-64" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} data-testid="staff-file-input" />
          <Button size="sm" disabled={!file || busy} onClick={doPreview} data-testid="staff-preview-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check file"}</Button>
        </div>
        {preview && <>
          <div className="text-sm" data-testid="staff-preview-summary"><b className="text-emerald-700">{preview.valid} ready</b> · <b className="text-rose-700">{preview.invalid} with problems (skipped)</b></div>
          <PreviewTable rows={preview.rows} />
          <Button disabled={!preview.valid || busy} onClick={doImport} data-testid="staff-import-confirm-btn">Add {preview.valid} staff</Button>
        </>}
        {created && <div className="space-y-2" data-testid="staff-import-result">
          <p className="text-sm font-semibold">{created.length} staff added. Temporary passwords are shown only once. Download them and share privately.</p>
          <div className="max-h-48 overflow-auto text-xs font-mono border rounded p-2">{created.map((c) => <div key={c.employee_id} data-testid="staff-created-row">{c.employee_id} · {c.name} · {c.temp_password}</div>)}</div>
          <Button size="sm" onClick={() => downloadPasswords(created)} data-testid="staff-passwords-download-btn"><Download className="h-4 w-4 mr-1" />Download passwords</Button>
        </div>}
      </DialogContent>
    </Dialog>
  );
}
